import { useRef, useEffect, useState, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useSearchParams } from 'next/navigation';
import { Search } from '@geberit/gdds';

// types
import type {
  AutocompletePrediction,
  AutoCompleteProps,
  AutocompleteService,
  Suggestion,
  SuggestionOption,
} from './autocomplete.types';

// utils
import { GeocoderStatus } from 'components/ContentElementsGdds/locator/showroom/constants';
import { locatorEvent } from 'components/ContentElementsGdds/locator/showroom/tracking-actions';
import { useTracking } from 'utils/hooks/useTracking';
import { decodingContent } from 'utils/decodingContent';
import { Log } from 'services/log';
import { isEmpty } from 'utils/is-empty';
import { buildOptions, buildPromiseCreator, normalizeSuggests } from './utils';

function Autocomplete({
  queryDelay = 250,
  minLength = 3,
  radius = 0,
  country = '',
  map,
  coordinates,
  googleMapsRef,
  searchPlaceholder = '',
  maxDistance,
  mode,
  setCurrentCoords,
  setGeocoded,
  setMaxDistance,
}: Readonly<AutoCompleteProps>) {
  const track = useTracking();
  const autocompleteService = useRef<AutocompleteService>(undefined);
  const [geocoderService, setGeocoderService] = useState<google.maps.Geocoder | null>(null);
  const [userInput, setUserInput] = useState<string | undefined>('');
  const [searchParam, setSearchParam] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const searchParams = useSearchParams();

  function clearSuggestions() {
    setSuggestions(null);
  }

  useEffect(() => {
    const location = searchParams.get('location');
    if (location) {
      setUserInput(location as string);
      setSearchParam(true);
    }
  }, [searchParams]);

  /**
   * Show all suggestions
   */
  const processInput = () => {
    if (userInput) {
      searchSuggestsByUserInput(userInput);
    } else {
      clearSuggestions();
    }
  };

  const processInputCallback = useDebouncedCallback(processInput, queryDelay);

  useEffect(() => {
    if (!isEmpty(googleMapsRef)) {
      autocompleteService.current = new googleMapsRef.places.AutocompleteService();
      const service = new googleMapsRef.Geocoder();
      setGeocoderService(service);
    }
  }, [googleMapsRef]);

  /**
   * Geocode a suggest
   * @param {Object} suggest - The selected suggest.
   */
  const geocodeSuggest = useCallback(
    async (suggest?: Suggestion) => {
      const suggestOption = { ...suggest } as SuggestionOption;

      let options: google.maps.GeocoderRequest = {};
      if (suggest?.placeId) {
        options = { placeId: suggest.placeId };
      } else if (suggest) {
        options = {
          address: suggest.label,
          bounds: map?.getBounds(),
        };
      }
      if ((options?.componentRestrictions?.country?.length ?? 0) > 5) {
        delete options?.componentRestrictions?.country;
      }

      if (geocoderService) {
        geocoderService.geocode(options, (results, status) => {
          if (status === GeocoderStatus.OK && results) {
            const details = results[0];
            const { location } = details.geometry;
            suggestOption.location = {
              lat: location.lat(),
              lng: location.lng(),
            };

            setCurrentCoords({
              lat: location.lat(),
              lng: location.lng(),
            });
            setGeocoded(true);

            // don't reset distance if it is already selected
            if (maxDistance === 'none') {
              setMaxDistance(50000);
            }
          }
        });
      }
    },
    [geocoderService, map, maxDistance, setCurrentCoords, setGeocoded, setMaxDistance],
  );

  /**
   * Search for predictions through the google autocomplete service.
   */
  const searchSuggestsByUserInput = useCallback(
    async (userInput?: string) => {
      try {
        // if we dont have a userInput, update the suggestions
        if (!userInput) {
          clearSuggestions();
          return;
        }

        // if the userinput length is less than the required stringlength, return
        if (userInput && userInput.length < minLength) {
          return;
        }

        if (!autocompleteService.current) {
          return;
        }

        // build the getPlacePredictions options object
        const options =
          coordinates &&
          buildOptions({
            userInput,
            googleMapsRef,
            coordinates,
            radius,
            mode,
            country,
          });

        const createPromise = buildPromiseCreator(
          autocompleteService.current,
          options ?? { input: userInput },
        );

        const placeTypes = ['(regions)', 'address'];
        const promises = placeTypes.map((type) => createPromise(type));

        // Await the getPlacePredictions by wrapping the default google request method with
        // a new promise wrapper.
        const predictions = await Promise.all(promises);
        const allResults: AutocompletePrediction[] = [];
        predictions.forEach((result) => {
          if (result.payload) allResults.push(...result.payload);
        });

        const normalizedSuggests = normalizeSuggests(allResults);
        setSuggestions(normalizedSuggests);
      } catch (error) {
        Log.error('Failed to get suggestions', error);
      }
    },
    [coordinates, country, googleMapsRef, minLength, mode, radius],
  );

  /**
   * Selects a suggest item and persists it in state.
   * @param {Event} e - The event object.
   * @param {Object} suggest - The selected suggest.
   */
  const selectSuggest = useCallback(
    (_?: Event, suggest?: Suggestion) => {
      // Cancel if we don't have a user input
      if (isEmpty(userInput)) {
        return;
      }

      // When we dont have a suggest, use the first google autosuggest
      // (e.g. when the user hits enter or uses the search-button)
      if (isEmpty(suggest) && !isEmpty(suggestions)) {
        geocodeSuggest(suggestions[0]);
        return;
      }

      if (!isEmpty(suggest)) {
        setUserInput(suggest.description);
      } else {
        setUserInput(undefined);
      }

      geocodeSuggest(suggest);
    },
    [geocodeSuggest, suggestions, userInput],
  );

  /**
   * process input on userinput change
   */
  useEffect(() => {
    if (!searchParam) {
      processInputCallback();
    }

    if (searchParam && userInput && userInput.length >= minLength && geocoderService) {
      track.trackEvent(locatorEvent('Locator-Search', userInput));
      searchSuggestsByUserInput(userInput);
    } else if (!userInput) {
      clearSuggestions();
    }
  }, [
    userInput,
    geocoderService,
    searchParam,
    minLength,
    processInputCallback,
    track,
    searchSuggestsByUserInput,
  ]);

  useEffect(() => {
    if (searchParam && !isEmpty(suggestions)) {
      selectSuggest();
      // should only trigger once the suggestion directly
      setSearchParam(false);
    }
  }, [searchParam, selectSuggest, suggestions]);

  return (
    <Search
      suggestions={(suggestions ?? []).map((s) => ({
        content: decodingContent(s.description),
      }))}
      placeholder={decodingContent(searchPlaceholder)}
      name="mainSearch"
      val={userInput}
      outlined
      onChange={setUserInput}
      onSubmit={(value) => {
        if (suggestions) {
          const sugg = suggestions.find((s) => value.toLowerCase() === s.description.toLowerCase());
          selectSuggest(undefined, sugg);
        }
      }}
    />
  );
}

export default Autocomplete;
